import Store from 'store/store';
import { accessNested } from 'utils/utils';

// "yyyy-MM-dd" => timestamp
function getDateTimestamp(str) {
	if (!str) return undefined;
	let match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!match) return undefined;
	return +new Date(+match[1], match[2] - 1, +match[3]);
}
// "yyyy-MM-dd to yyyy-MM-dd" => [timestampFrom, timestampTo]
function getDateRange(str) {
	let match = str.match(/^((?:!|not)\s*)?(\S+)(?:\s*(?:to|->)\s*(\S+))?$/i);
	if (!match) return undefined;
	let from = getDateTimestamp(match[2]);
	let to = match[3] ? getDateTimestamp(match[3]) : from;
	if (!from || !to) return undefined;
	return [from, to, !!match[1]];
}
function inRange(value, range) {
	return range && (value >= range[0] && value <= range[1]) === !range[2];
}

function pad2(n) {
	if (!+n) n = 0;
	return ('0' + n).slice(-2);
}
function cleanTime(time) {
	if (!time || typeof time !== 'string') return null;
	let parts = time.match(/^(\d{1,2})(?::(\d{1,2})(?::(\d{1,2}))?)?$/);
	if (!parts) return null;
	return pad2(parts[1]) + ':' + pad2(parts[2]) + ':' + pad2(parts[3]);
}
function getTheaterId(identifier, included) {
	if (!identifier || typeof identifier !== 'string') return null;
	let parts = identifier.split(':');
	if (parts.length < 2) {
		return identifier;
	}
	let pid = parts.shift();
	let ptid = parts.join(':');
	let match = included && included.find(inc => inc.type === 'theaters' && inc.attributes.provider_id === pid && inc.attributes.provider_theater_id === ptid);
	return match && match.id;
}

let caches = {};
function getIncluded(included, type, id) {
	if (!included || !type || !id) return;
	if (!caches[type]) {
		caches[type] = {};
	}
	if (caches[type][id]) {
		return caches[type][id];
	}
	return caches[type][id] = included.find(inc => inc.type === type && inc.id === id);
}

let formatCache = {};
let movieCache = {};
function editFormat(included, screening, format) {
	if (!included || !screening || !format) return;
	if (typeof format === 'string') {
		let search = format.toLowerCase();
		if (formatCache[search]) {
			format = formatCache[search];
		} else {
			let result = included.find(inc => (
				inc.type === 'formats' && inc.attributes.name.toLowerCase() === search
			));
			if (!result) {
				result = {
					type: 'formats',
					id: search,
					attributes: { name: format }
				};
				included.push(result);
			}
			formatCache[search] = format = result;
		}
	}
	if (!format || !format.id) return;
	let mid = screening.attributes.movie_id;
	let srcMovie = included.find(inc => (
		inc.type === 'movies' && inc.id === mid
	));
	if (!srcMovie || srcMovie.attributes.format_id === format.id) return;
	let lookup = [srcMovie.attributes.provider_id, format.id].join('|');
	let movie = movieCache[lookup];
	if (!movie) {
		movie = included.find(inc => (
			inc.type === 'movies' && inc.attributes.format_id === format.id && inc.attributes.provider_id === srcMovie.attributes.provider_id
		));
		if (!movie) {
			// Urgh
			movie = JSON.parse(JSON.stringify(srcMovie));
			movie.attributes.format_id = format.id;
			movie.id += '|' + format.id;
			included.push(movie);
		}
		movieCache[lookup] = movie;
	}
	if (!movie || !movie.id) return;
	screening.attributes.movie_id = movie.id;
}

function clearCaches() {
	caches = {};
	formatCache = {};
	movieCache = {};
}

function formatSlug(name) {
	if (!name) return name;
	return name.toLowerCase().replace(/ /g, '');
}

export default function(data, included, appData) {
	clearCaches();

	let store = Store.get();
	if (accessNested(store, 'activePage.data.splitSpecialTheaters', true)) {
		let thCache = {};
		let splitTheater = (id, data) => {
			let nid = id + ':' + data.indicator;
			if (thCache[nid]) {
				return thCache[nid];
			}
			let src = included.find(i => i.type === 'theaters' && i.id === id);
			if (!src) return;
			let nTh = JSON.parse(JSON.stringify(src));
			nTh.id = nid;
			nTh.attributes.name += ' (' + data.name + ')';
			nTh.attributes.theater_master_id += ':' + data.indicator;
			nTh.attributes.theater_master_id = data.masterId || (nTh.attributes.theater_master_id + ':' + data.indicator);
			thCache[nid] = nTh;
			included.push(nTh);
			return nTh;
		};
		let specialTheaters = [
			{ indicator: 'dome', name: 'Dome' },
			// Hardcoded Curzon Bloomsbury dochouse id - ugly I know but "aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee:dochouse" is not a valid guid
			// And need to match powster dochouse to the split version of webedia
			{ indicator: 'dochouse', name: 'DocHouse', masterId: '98e24e12-d9bb-11e7-9354-db0c57537af4' }
		];
		data.forEach(s => {
			let comments = accessNested(s, 'attributes.doc.comments');
			if (!comments) return;
			let commentsList = comments.toLowerCase().split(';');
			// Using find to have a break behaviour (stops after the first found)
			specialTheaters.find(special => {
				if (commentsList.indexOf(special.indicator) === -1) {
					return;
				}
				let newTheater = splitTheater(s.attributes.theater_id, special);
				if (newTheater) {
					s.attributes.theater_id = newTheater.id;
				}
			});
		});
	}
	if (accessNested(store, 'activePage.data.usePowAsVirtualCinemas')) {
		included.forEach(inc => {
			if (inc.type !== 'theaters' || inc.attributes.provider_id !== 'pow') {
				return;
			}
			let firstScreening = data.find(s => s.attributes.theater_id === inc.id);
			if (firstScreening && firstScreening.attributes.cta) {
				inc.attributes.theater_master_id = null;
				inc.attributes.name += ' [VIRTUAL CINEMA]';
			}
		});
	}

	let disabledScreenings = accessNested(store, 'activePage.data.disabledScreenings') || {};
	let disabledScreeningsContexts = [disabledScreenings].concat(
		Object.keys(disabledScreenings).map(k => disabledScreenings[k])
	).filter(ctx => ctx && Object.keys(ctx).length);

	if (disabledScreeningsContexts.length) {
		disabledScreeningsContexts = disabledScreeningsContexts.map(ctx => {
			ctx = Object.assign({}, ctx);
			if (ctx.date) {
				ctx.date = ctx.date.split(',').map(part => getDateRange(part.trim())).filter(e => e);
			}
			if (ctx.time) {
				ctx.time = ctx.time.split(',').map(part => cleanTime(part.trim())).filter(e => e);
			}
			if (ctx.format) {
				ctx.format = ctx.format.split(',').map(part => formatSlug(part.trim())).filter(e => e);
			}
			if (ctx.theater) {
				ctx.theater = ctx.theater.split(',').map(part => getTheaterId(part.trim(), included)).filter(e => e);
			}
			if (ctx.provider) {
				ctx.provider = ctx.provider.split(',').map(part => part.trim()).filter(e => e);
			}
			if (ctx.exhibitor) {
				ctx.exhibitor = ctx.exhibitor.split(',').map(part => part.trim()).filter(e => e);
			}
			return ctx;
		});
		let localFormatCache = {};
		let getFormat = screening => {
			let mid = screening.attributes.movie_id;
			if (localFormatCache[mid]) {
				return localFormatCache[mid];
			}
			let format = null;
			let movie = included.find(inc => inc.type === 'movies' && inc.id === mid);
			if (movie && movie.attributes.format_id) {
				let fid = movie.attributes.format_id;
				let fObj = included.find(inc => inc.type === 'formats' && inc.id === fid);
				format = fObj && fObj.attributes.name;
			}
			return localFormatCache[mid] = format;
		};
		data = data.filter(screening => {
			// If one context validates, filter out the screening
			return !disabledScreeningsContexts.find(ctx => {
				return Object.keys(ctx).every(key => {
					let val = ctx[key];
					if (val === null) {
						return true;
					}
					switch (key) {
						case 'date':
							let date = getDateTimestamp((screening.attributes.date || '').slice(0, 10));
							return date && ctx.date.find(range => inRange(date, range));
						case 'time':
							let time = screening.attributes.time;
							return time && ctx.time.indexOf(time) !== -1;
						case 'format':
							let slug = formatSlug(getFormat(screening));
							return slug && ctx.format.indexOf(slug) !== -1;
						case 'theater':
							let tid = screening.attributes.theater_id;
							return tid && ctx.theater.indexOf(tid) !== -1;
						case 'provider':
							let provider = screening.attributes.provider_id;
							return provider && ctx.provider.indexOf(provider) !== -1;
						case 'exhibitor':
							let thId = screening.attributes.theater_id;
							let theater = getIncluded(included, 'theaters', thId);
							let exId = accessNested(theater, 'attributes.exhibitor_id');
							let exhibitor = getIncluded(included, 'exhibitors', exId);
							let domain = accessNested(exhibitor, 'attributes.domain');
							return domain && ctx.exhibitor.indexOf(domain) !== -1;
					}
				});
			});
		});
	}

	let customFormats = accessNested(store, 'activePage.data.customFormats') || {};
	if (customFormats.format) {
		customFormats = {
			main: customFormats
		};
	}
	// Prebuild the object to make things faster
	let customFormatsList = Object.keys(customFormats).map((prop, index) => {
		let data = customFormats[prop];
		if (!data || typeof data !== 'object' || !data.format) return;
		let dataObj = {
			format: data.format,
			append: !!data.append
		};
		if (data.date) {
			dataObj.dateRanges = data.date.split(',').map(part => getDateRange(part.trim())).filter(e => e);
		}
		if (data.time) {
			dataObj.times = data.time.split(',').map(time => cleanTime(time.trim())).filter(e => e);
		}
		if (data.screening) {
			dataObj.screenings = data.screening.split(',').map(screening => screening.trim()).filter(e => e);
		}
		// if (data.theaterName) {
		// Issue with theaters having a comma in the name
		// 	dataObj.theaterName = data.theaterName.split(',');
		// }
		if (data.theater) {
			dataObj.theaters = data.theater.split(',').map(theater => theater.trim()).filter(e => e);
		} else  if (data.theaterNot) {
			dataObj.theatersNot = data.theaterNot.split(',').map(theater => theater.trim()).filter(e => e);
		}
		if (data.domain) {
			dataObj.domains = data.domain.split(',').map(domain => domain.trim()).filter(e => e);
		}
		if (data.country) {
			dataObj.countries = data.country.split(',').map(country => country.trim()).filter(e => e);
		}
		dataObj.order = data.order || (index + 100);
		return dataObj;
	}).filter(e => e);
	customFormatsList.sort((a, b) => a.order - b.order);
	if (customFormatsList.length) {
		data.forEach(screening => {
			let sDate = getDateTimestamp((screening.attributes.date || '').slice(0, 10));
			let sTime = screening.attributes.time;
			let matching = customFormatsList.find(formatCond => {
				if (formatCond.screenings) {
					if (!formatCond.screenings.includes(screening.id)) {
						return false;
					}
				}
				if (formatCond.dateRanges) {
					if (!sDate || !formatCond.dateRanges.find(range => inRange(sDate, range))) {
						return false;
					}
				}
				if (formatCond.times) {
					if (!sTime || formatCond.times.indexOf(sTime) === -1) {
						return false;
					}
				}
				if (formatCond.theaters || formatCond.theatersNot) {
					let thId = screening.attributes.theater_id;
					let theater = getIncluded(included, 'theaters', thId);
					let ptid = accessNested(theater, 'attributes.provider_id') + ':' + accessNested(theater, 'attributes.provider_theater_id');
					if (
						(formatCond.theaters && !formatCond.theaters.find(desired => thId === desired || ptid === desired))
						|| (formatCond.theatersNot && formatCond.theatersNot.find(desired => thId === desired || ptid === desired))
					) {
						return false;
					}
				}
				if (formatCond.domains) {
					let thId = screening.attributes.theater_id;
					let theater = getIncluded(included, 'theaters', thId);
					let exId = accessNested(theater, 'attributes.exhibitor_id');
					let exhibitor = getIncluded(included, 'exhibitors', exId);
					let domain = accessNested(exhibitor, 'attributes.domain');
					if (!domain || formatCond.domains.indexOf(domain) === -1) {
						return false;
					}
				}
				if (formatCond.countries) {
					let thId = screening.attributes.theater_id;
					let theater = getIncluded(included, 'theaters', thId);
					let country = accessNested(theater, 'attributes.country');
					if (!country || formatCond.countries.indexOf(country) === -1) {
						return false;
					}
				}
				return true;
			});
			if (matching) {
				let format = matching.format;
				if (matching.append) {
					let movie = getIncluded(included, 'movies', screening.attributes.movie_id);
					let formatObject = getIncluded(included, 'formats', movie && movie.attributes.format_id);
					if (formatObject) {
						format = formatObject.attributes.name + ' ' + format;
					}
				}
				editFormat(included, screening, format);
			}
		});
	}

	return {
		data: data,
		included: included
	};
}

export {
	getDateTimestamp,
	getDateRange,
	inRange,
	editFormat,
	getIncluded,
	clearCaches
};
